import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { LANDSCAPE_PHONE_QUERY, USABLE_MIN_PX } from "src/utils/viewport"

import "./MobileGate.css"

/* What is left of the gate once every width has a layout.

   It has come down twice and for opposite reasons. At 700px it was wrong: the two fixed docks are
   at floored clamps and charge 690px before the map gets a pixel, so at 701px the map was **11
   pixels** and a 768px portrait tablet got 78 — three cells of a 200x100 world. Raising it to
   1000 made it honest about the shell it was guarding. Then the compact shell arrived and it fell
   to 744, and now the phone shell — no docks at all, the map as the floor, the panels as sheets —
   leaves nothing between here and the bottom.

   So what it guards now is the last thing no layout can fix: a window with no room for a map. The
   phone shell spends nothing on chrome, so that is simply the floor itself, twelve cells. Below it
   there is no arrangement of panels that helps, which is exactly when telling somebody up front
   beats showing them a broken screen they will blame on themselves.

   Width, not user agent, as it always was. */
const PHONE_QUERY = `(max-width: ${USABLE_MIN_PX - 1}px)`

export { PHONE_QUERY }

// jsdom has no matchMedia; tests render the app, never the gate.
const useMediaQuery = (query) => {
  const supported = typeof window.matchMedia === "function"
  const [matches, setMatches] = useState(() => supported && window.matchMedia(query).matches)

  useEffect(() => {
    if (!supported) return undefined
    const media = window.matchMedia(query)
    const onChange = (event) => setMatches(event.matches)
    setMatches(media.matches)
    media.addEventListener("change", onChange)
    return () => media.removeEventListener("change", onChange)
  }, [supported, query])

  return matches
}

export const useIsPhoneSized = () => useMediaQuery(PHONE_QUERY)

/* A phone held sideways, which the game does not play in.
 *
 * The refusal is a design decision rather than a defect: every vertical composition here — the
 * hero, the map's rail, the bottom sheets, the tab bar — is built for a portrait phone, and a
 * landscape one has about 250px of map under 128px of chrome. It was worth the pass that made
 * landscape survivable, because a window can be that shape for a second while somebody turns
 * their phone back; it is not worth designing a second game for.
 *
 * Distinct from the size gate above in one way that matters: this one is **transient**. Somebody
 * turns their phone by accident mid-round, and unmounting the app would drop the socket and the
 * march they were halfway through drawing. So App renders this *over* the app rather than instead
 * of it, and turning back gives the exact screen they left. */
export const useIsPhoneLandscape = () => useMediaQuery(LANDSCAPE_PHONE_QUERY)

function MobileGate({ reason = "size" }) {
  const { t } = useTranslation()
  const landscape = reason === "landscape"

  return (
    <div className={`mobile-gate${landscape ? " mobile-gate-landscape" : ""}`} role="alertdialog" aria-live="polite">
      {landscape && (
        <span className="mobile-gate-turn" aria-hidden="true">
          ⟳
        </span>
      )}
      <p className="mobile-gate-title">
        {landscape ? t("app.rotateTitle") : t("app.phoneUnsupportedTitle")}
      </p>
      <p className="mobile-gate-body">
        {landscape ? t("app.rotateBody") : t("app.phoneUnsupportedBody")}
      </p>
    </div>
  )
}

export default MobileGate
